import { useEffect, useState } from "react";

import KOGCollapse from "../kogCollapse/kogCollapse";
import KOGPaginationGrid from "../paginationGrid/paginationGrid";
import PartOriginInfo from "../partOriginInfo/partOriginInfo";
import AttachmentCard from "./attachmentCard";

import { PaginationRequest } from "@/main/models/common/paginationRequest";
import { PaginationResponse } from "@/main/models/common/paginationResponse";
import { PartOriginPriceResponse } from "@/main/models/response";
import { PaginationEnum } from "@/main/services/enum/PaginationEnum";
import { getDefaultMessageResultsFound } from "@/main/services/utils";
import { PartAttachment } from "./evidenceUtils";

import * as S from "./styles";

interface IPartAttachmentsFilters extends PaginationRequest {
	reloadTrigger?: number;
}

interface IPartAttachmentsCollapseProps {
	quoteItemGuid: string;
	title: string;
	originPrice?: PartOriginPriceResponse;
	attachments: PartAttachment[];
	canDownload: boolean;
	expanded: boolean;
	reloadTrigger?: number;
	onToggle: (isExpanded: boolean) => void;
	onDelete: (attachment: PartAttachment) => void;
	onDownload: (attachment: PartAttachment) => void;
	onPreview: (index: number, attachments: PartAttachment[]) => void;
}

function PartAttachmentsCollapse({
	quoteItemGuid,
	title,
	originPrice,
	attachments,
	canDownload,
	expanded,
	reloadTrigger,
	onToggle,
	onDelete,
	onDownload,
	onPreview,
}: IPartAttachmentsCollapseProps) {
	const [dataGrid, setDataGrid] = useState<PartAttachment[]>([]);
	const [totalRecords, setTotalRecords] = useState<number>(0);
	const [formFilters, setFormFilters] = useState<IPartAttachmentsFilters | null>(null);

	const idScrollableTarget = `id-part-attachments-scroll-${quoteItemGuid}`;

	function clearPages() {
		setDataGrid([]);
		setTotalRecords(0);
	}

	useEffect(() => {
		clearPages();
		setFormFilters({
			pageIndex: PaginationEnum.PageIndexIntial,
			pageSize: PaginationEnum.PageSize10,
			totalNumberOfRecords: attachments.length,
			reloadTrigger,
		});
	}, [quoteItemGuid, attachments, reloadTrigger]);

	function handleToggle(isExpanded: boolean) {
		if (!isExpanded) clearPages();
		onToggle(isExpanded);
	}

	function fetchPage(request: IPartAttachmentsFilters): Promise<PaginationResponse<PartAttachment>> {
		const pageIndex = request.pageIndex ?? PaginationEnum.PageIndexIntial;
		const pageSize = request.pageSize ?? PaginationEnum.PageSize10;
		const startIndex = (pageIndex - 1) * pageSize;

		return Promise.resolve({
			isHideToastNotification: true,
			pageNumber: pageIndex,
			pageSize,
			totalNumberOfPages: Math.ceil(attachments.length / pageSize),
			totalNumberOfRecords: attachments.length,
			results: attachments.slice(startIndex, startIndex + pageSize),
		});
	}

	return (
		<KOGCollapse
			isOpenCollapseinitial={expanded}
			forceCollapseTrigger={!expanded}
			onCustomCollapse={handleToggle}
			header={
				<S.PartAttachmentsHeader>
					<span style={{ fontWeight: "bold" }}>{title}</span>
					<PartOriginInfo data={originPrice} idIconButton={`id-part-attachment-origin-${quoteItemGuid}`} />
				</S.PartAttachmentsHeader>
			}
			content={
				<S.EvidenceGroupContent>
					<S.PaginationLabel id={`id-part-attachments-count-${quoteItemGuid}`}>
						{totalRecords > 0 && getDefaultMessageResultsFound(dataGrid.length, totalRecords)}
					</S.PaginationLabel>

					<S.PartAttachmentsScroll id={idScrollableTarget}>
						<KOGPaginationGrid<PartAttachment, IPartAttachmentsFilters>
							idScrollableTarget={idScrollableTarget}
							showTitleResults={false}
							showEndMessage={false}
							totalRecords={totalRecords}
							setTotalRecords={setTotalRecords}
							dataGrid={dataGrid}
							setDataGrid={setDataGrid}
							formFilters={formFilters}
							fetchMore={fetchPage}
						>
							<S.ContainerPreview previewImage="column">
								{dataGrid.map((attachment, index) => (
									<AttachmentCard
										key={attachment.guid}
										ids={{
											card: `id-part-attachment-item-${attachment.guid}`,
											preview: `id-part-attachment-preview-${attachment.guid}`,
											deleteButton: `id-part-attachment-btn-delete-${attachment.guid}`,
											downloadButton: `id-part-attachment-btn-download-${attachment.guid}`,
											comment: `id-part-attachment-comment-${attachment.guid}`,
										}}
										fileName={attachment.fileName}
										comment={attachment.comment}
										isImage={attachment.isImage}
										imageSource={attachment.externalKey}
										fileExtension={attachment.fileExtension}
										canDelete={attachment.canDelete}
										canDownload={canDownload}
										onDelete={() => onDelete(attachment)}
										onDownload={() => onDownload(attachment)}
										onPreview={() => onPreview(index, attachments)}
									/>
								))}
							</S.ContainerPreview>
						</KOGPaginationGrid>
					</S.PartAttachmentsScroll>
				</S.EvidenceGroupContent>
			}
		/>
	);
}

export default PartAttachmentsCollapse;
